// components/connexion/ecransPassage.js
// ============================================================================
// ÉCRANS D'ACCUEIL ET D'AU REVOIR — TEXTES, HORAIRES, REPÈRES
// ============================================================================
//
// Ce fichier existe à l'identique dans KLASYS (src/components/connexion/) et
// dans le CIK (frontend/src/components/ecrans/) : le CIK en a besoin pour
// l'aperçu, KLASYS pour les vrais écrans. Modifier l'un, c'est modifier l'autre.
//
// Les textes se règlent dans le CIK (rubrique `ecrans` de
// `klasys_tenants.page_connexion`, migration CIK 017). Ils portent des repères
// remplacés à l'affichage : {civilite}, {nom}, {ecole}, {heure}.
//
// Journée ou soirée : décidé par l'heure de l'appareil qui affiche l'écran,
// comparée aux deux horaires réglés.
// ============================================================================

export const ECRANS_PAR_DEFAUT = {
  horaires: { debut_journee: '06:00', debut_soiree: '18:00' },
  accueil: {
    jour: 'Bonjour {civilite} {nom}',
    soir: 'Bonsoir {civilite} {nom}',
    ecole: 'Vous vous connectez à {ecole}',
    heure: 'Il est {heure}'
  },
  accueil_kiosque: {
    jour: 'Bonjour',
    soir: 'Bonsoir',
    titre: 'Connexion au Kiosque de Pointage'
  },
  au_revoir: {
    jour: 'Au revoir {civilite} {nom}',
    soir: 'Bonne soirée {civilite} {nom}',
    fin: 'À bientôt !'
  },
  au_revoir_kiosque: {
    jour: 'Au revoir',
    soir: 'Bonne soirée',
    fin: 'À bientôt !'
  }
};

const HEURE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Fusionne une configuration reçue avec les valeurs du code.
 * Un texte vide ou absent, des horaires invalides : la valeur du code reste.
 */
export function fusionnerEcrans(config) {
  const d = ECRANS_PAR_DEFAUT;
  const c = config && typeof config === 'object' ? config : {};

  const h = c.horaires || {};
  const horairesValides = HEURE.test(h.debut_journee || '') && HEURE.test(h.debut_soiree || '')
    && h.debut_journee < h.debut_soiree;

  const groupe = (nom) => Object.fromEntries(
    Object.keys(d[nom]).map((cle) => {
      const v = c[nom]?.[cle];
      return [cle, typeof v === 'string' && v.trim() ? v : d[nom][cle]];
    })
  );

  return {
    horaires: horairesValides ? { debut_journee: h.debut_journee, debut_soiree: h.debut_soiree } : { ...d.horaires },
    accueil: groupe('accueil'),
    accueil_kiosque: groupe('accueil_kiosque'),
    au_revoir: groupe('au_revoir'),
    au_revoir_kiosque: groupe('au_revoir_kiosque')
  };
}

const enMinutes = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** 'jour' entre le début de la journée (inclus) et le début de la soirée, sinon 'soir'. */
export function momentPour(horaires, date = new Date()) {
  const { debut_journee, debut_soiree } = horaires || ECRANS_PAR_DEFAUT.horaires;
  const t = date.getHours() * 60 + date.getMinutes();
  return t >= enMinutes(debut_journee) && t < enMinutes(debut_soiree) ? 'jour' : 'soir';
}

/**
 * Découpe un texte en morceaux, repères remplacés. Les repères listés dans
 * `gras` sortent en gras (le nom de l'école, comme sur l'écran d'origine).
 * Un repère sans valeur disparaît, avec l'espace en trop.
 */
export function segments(gabarit, valeurs = {}, gras = ['ecole']) {
  const morceaux = [];
  const motif = /\{(\w+)\}/g;
  let dernier = 0;
  let m;
  while ((m = motif.exec(gabarit)) !== null) {
    if (m.index > dernier) morceaux.push({ texte: gabarit.slice(dernier, m.index), gras: false });
    const valeur = valeurs[m[1]];
    if (valeur) morceaux.push({ texte: String(valeur), gras: gras.includes(m[1]) });
    dernier = motif.lastIndex;
  }
  if (dernier < gabarit.length) morceaux.push({ texte: gabarit.slice(dernier), gras: false });

  // Espaces doublés par un repère vide, espaces en bord de texte
  const propres = morceaux.map((x) => ({ ...x, texte: x.texte.replace(/\s{2,}/g, ' ') }));
  if (propres.length) {
    propres[0].texte = propres[0].texte.replace(/^\s+/, '');
    propres[propres.length - 1].texte = propres[propres.length - 1].texte.replace(/\s+$/, '');
  }
  for (let i = 1; i < propres.length; i += 1) {
    if (propres[i - 1].texte.endsWith(' ') && propres[i].texte.startsWith(' ')) {
      propres[i].texte = propres[i].texte.slice(1);
    }
  }
  const restants = propres.filter((x) => x.texte);
  // Un repère vide en fin de texte laisse un espace sur le morceau d'avant
  if (restants.length) {
    const fin = restants.length - 1;
    restants[fin] = { ...restants[fin], texte: restants[fin].texte.replace(/\s+$/, '') };
    restants[0] = { ...restants[0], texte: restants[0].texte.replace(/^\s+/, '') };
  }
  return restants.filter((x) => x.texte);
}

/** Le texte à plat, repères remplacés. */
export const remplir = (gabarit, valeurs) => segments(gabarit, valeurs, []).map((x) => x.texte).join('');

export const civiliteDe = (sexe) => (sexe === 'M' || sexe === 'Masculin' ? 'Monsieur' : 'Madame');

export const heureDe = (date = new Date()) =>
  date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
